import { useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';

interface SegmentedControlProps {
  tab1Label: string;
  tab2Label: string;
  tab3Label?: string;
  onPressTab1: () => void;
  onPressTab2: () => void;
  onPressTab3?: () => void;
  activeIdx?: number;
  style?: StyleProp<ViewStyle>;
}

export const SegmentedControl = ({
  tab1Label,
  tab2Label,
  tab3Label,
  onPressTab1,
  onPressTab2,
  onPressTab3,
  activeIdx = 0,
  style,
}: SegmentedControlProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const [activeTabIdx, setActiveTabIdx] = useState(activeIdx);

  const tabs = [
    { label: tab1Label, onPress: onPressTab1 },
    { label: tab2Label, onPress: onPressTab2 },
    ...(tab3Label ? [{ label: tab3Label, onPress: onPressTab3 }] : []),
  ];

  return (
    <View style={[styles.container, style]}>
      {tabs.map((tab, idx) => {
        const isActive = activeTabIdx === idx;

        return (
          <Pressable
            key={tab.label}
            onPress={() => {
              tab.onPress?.();
              setActiveTabIdx(idx);
            }}
            hitSlop={8}
            style={[
              styles.button,
              {
                backgroundColor: isActive
                  ? theme.colors.alert.brand.backgroundColor
                  : 'transparent',
                borderWidth: isActive ? 1 : 0,
                borderColor: isActive
                  ? theme.colors.alert.brand.border
                  : 'transparent',
              },
            ]}
          >
            <Text type="bodyRegular" style={styles.buttonText}>
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexDirection: 'row',
    gap: 8,
    padding: 8,
    borderRadius: 12,
    backgroundColor: theme.colors.card.hover.backgroundColor,
    alignItems: 'center',
    height: 60,
  },
  button: {
    flex: 1,
    width: 'auto',
    borderRadius: 12,
    borderColor: 'transparent',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8,
    paddingRight: 8,
  },
  buttonText: {
    fontSize: 15,
    textAlign: 'center',
  },
}));
